import type { useGT } from 'gt-react';
import {
  DatabaseIcon,
  FileArrowUpIcon,
  ListChecksIcon,
  TableIcon,
} from '@phosphor-icons/react';
import type {
  WizardStepDef,
  WizardValidationResult,
} from '@erp/ui/hooks/use-wizard';
import {
  mappingStepProblems,
  schemaStepDecision,
  sourceStepDecision,
  tableStepDecision,
} from './wizardStepChecks';
import type { DataMappingWizardState } from './useDataMappingWizardState';

/**
 * The wizard's four steps, built from the current state. Each `validate` reads a
 * decision from the tested pure checks and only owns the side effect that goes
 * with it (uploading, or recording field errors).
 */
export function buildDataMappingWizardSteps(
  state: DataMappingWizardState,
  gt: ReturnType<typeof useGT>,
): WizardStepDef[] {
  return [
    {
      id: 'source',
      title: gt('Source'),
      description: gt(
        'Upload the file this mapping comes from, or skip and fill the table from a workflow instead.',
      ),
      icon: FileArrowUpIcon,
      optional: true,
      validate: async (): Promise<WizardValidationResult> => {
        const decision = sourceStepDecision({
          sourceMode: state.sourceMode,
          hasSource: !!state.source,
          hasFile: !!state.file,
          existingAssetIsUsable: state.readyAssets.some(
            (asset) => asset.id === state.existingAssetId,
          ),
        });
        if (decision === 'need-existing-asset') {
          return {
            ok: false,
            fields: {
              existingAssetId: gt('Pick an asset that has a version.'),
            },
          };
        }
        if (decision === 'need-file') {
          return {
            ok: false,
            fields: { file: gt('Choose a file, or skip this step.') },
          };
        }
        if (decision === 'upload') {
          const uploaded = await state.uploadSource();
          return uploaded
            ? true
            : { ok: false, message: gt('The upload did not finish.') };
        }
        // Reachable only as "ok" — record the picked asset as the source.
        if (state.sourceMode === 'existing') state.applyExistingSource();
        return true;
      },
    },
    {
      id: 'schema',
      title: gt('Schema'),
      description: gt(
        'The columns this mapping holds. Each becomes a real column, and the description is what the column matcher reads.',
      ),
      icon: ListChecksIcon,
      validate: (): WizardValidationResult => {
        const decision = schemaStepDecision({
          tableMode: state.tableMode,
          columns: state.columns,
          keyColumnIds: state.keyColumnIds,
        });
        if (decision.ok) {
          state.setColumnErrors({});
          return true;
        }
        state.setColumnErrors(decision.errors);
        return {
          ok: false,
          message:
            decision.message ??
            gt('Fix the highlighted columns before continuing.'),
        };
      },
    },
    {
      id: 'table',
      title: gt('Stateful table'),
      description: gt(
        'Where the rows live. A new table is provisioned from the columns above.',
      ),
      icon: TableIcon,
      gate: true,
      confirm: () => ({
        title:
          state.tableMode === 'new'
            ? gt('Create a new stateful table?')
            : gt('Use the selected stateful table?'),
        detail:
          state.tableMode === 'new'
            ? gt(
                'This provisions a real database table and a matching schema definition.',
              )
            : gt(
                'This mapping will read and write the rows already in that table.',
              ),
        consequences:
          state.tableMode === 'new'
            ? [
                gt('Creates 1 stateful table with {count} columns', {
                  count: String(state.columns.length),
                }),
                gt('Creates 1 schema definition, version 1'),
              ]
            : [gt('Links to an existing table and its schema definition')],
      }),
      validate: (): WizardValidationResult => {
        const decision = tableStepDecision({
          tableMode: state.tableMode,
          existingTableId: state.existingTableId,
          tableName: state.tableName,
          mappingName: state.mappingName,
        });
        if (decision === 'ok') return true;
        return decision === 'need-existing-table'
          ? {
              ok: false,
              fields: { existingTableId: gt('Choose a stateful table.') },
            }
          : { ok: false, fields: { tableName: gt('Name the table.') } };
      },
    },
    {
      id: 'mapping',
      title: gt('Data mapping'),
      description: gt(
        'Name the mapping. The slug is what code resolves it by, so it should stay stable.',
      ),
      icon: DatabaseIcon,
      gate: true,
      confirm: () => ({
        title: gt('Create this data mapping?'),
        consequences: [
          `${gt('Slug')}: ${state.mappingSlug.trim() || '—'}`,
          state.source
            ? gt('Links the uploaded source file')
            : gt('No source file — rows come from workflows'),
          state.syncNow && state.source
            ? gt('Pushes the file into the table now')
            : gt('Leaves the table empty for now'),
        ],
        confirmLabel: gt('Create mapping'),
      }),
      validate: (): WizardValidationResult => {
        const problems = mappingStepProblems({
          mappingName: state.mappingName,
          mappingSlug: state.mappingSlug,
        });
        const fields: Record<string, string> = {};
        if (problems.name) fields.mappingName = gt('Name is required.');
        if (problems.slug === 'missing-slug') {
          fields.mappingSlug = gt('Slug is required.');
        } else if (problems.slug === 'bad-slug') {
          fields.mappingSlug = gt(
            'Lowercase letters, digits, hyphens, and underscores only.',
          );
        }
        return Object.keys(fields).length ? { ok: false, fields } : true;
      },
    },
  ];
}
